import React, { useEffect } from 'react';

import i18n from 'i18n-js';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import Loading from 'components/Loading';
import { Card, CollapseCard } from 'components/UI';
import { PaymentActions } from 'state/modules/payment';
import { TransactionsAction } from 'state/modules/transaction';
import { useDispatch, useSelector } from 'react-redux';
import { formatAmount, formatSourceName, getDynamicLink } from 'utils';
import { useStyles, listTextStyles, collapseCardStyles } from './PaymentWidget.styled';
import { Box, Grid, List, Typography, ListItem, ListItemText, Divider } from '@material-ui/core';
import { FETCH_STATE } from '../../constants/common';

const ListTitleComponent = ({ title, subtitle }) => {
  const classes = useStyles();
  return (
    <Box className={classes.titleRow}>
      <Typography component="span" className={classes.title}>
        {title}
      </Typography>
      <Typography component="span" className={classes.subtitle}>
        {subtitle}
      </Typography>
    </Box>
  );
};

const PaymentWidget = ({ id }) => {
  const classes = useStyles();
  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const dispatch = useDispatch();

  // Check if we need to call payments or transaction
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const core = useSelector((state) => state.config?.settings?.core || {});
  const hasPartnership = core?.partnership?.enabled || false;
  const isFFBEnabled = sharingType === 'FFB' && hasPartnership;
  const useTransactions = isFFBEnabled;

  const {
    payments,
    transactions,
    organizations: { localeFormat },
  } = useSelector((state) => ({
    payments: state.payment,
    transactions: state.transaction,
    organizations: state.organizations,
  }));

  const current = useTransactions ? transactions.current : payments.current;
  const fetchingCurrent = useTransactions
    ? transactions.fetchingCurrent === FETCH_STATE.LOADING
    : payments.fetchingCurrent;

  useEffect(() => {
    if (useTransactions) {
      dispatch(TransactionsAction.fetchTransactionById(id));
    } else {
      dispatch(PaymentActions.fetchPaymentById(id));
    }
  }, [id, dispatch]);

  const paymentData = (payment) => [
    {
      title: i18n.t('payments.information'),
      items: [
        {
          label: `${i18n.t('payments.customer')}:`,
          value: payment?.account_customer?.customer_name || '--',
        },
        {
          label: `${i18n.t('payments.method')}:`,
          value: i18n.t(`payment_methods.${payment?.source_type}`, {
            defaultValue: payment?.source_type,
          }),
        },
        {
          label: i18n.t('payments.brand') + ':',
          show: payment?.payment_method?.card?.brand,
          value: payment?.payment_method?.card?.brand,
        },
        {
          label: i18n.t('payments.brand') + ':',
          show: payment?.payment_method?.ach?.bank_name,
          value: payment?.payment_method?.ach?.bank_name,
        },
        {
          label: i18n.t('payments.card') + ':',
          show: payment?.payment_method?.card?.last_four,
          value: '**** **** **** ' + payment?.payment_method?.card?.last_four,
        },
        {
          label: i18n.t('payments.account_last_four_digits') + ':',
          show: payment?.payment_method?.ach?.last_four,
          value: '**** **** **** ' + payment?.payment_method?.ach?.last_four,
        },
        {
          label: i18n.t('payments.expiration') + ':',
          show: payment?.payment_method?.card?.exp_month && payment?.payment_method?.card?.exp_year,
          value:
            payment?.payment_method?.card?.exp_month +
            '/' +
            payment?.payment_method?.card?.exp_year,
        },
      ],
    },
    {
      title: i18n.t('payments.details'),
      items: [
        {
          label: `${i18n.t('common.date')}:`,
          value: payment?.modified_time
            ? moment(payment?.modified_time).format(`${localeFormat}`)
            : '--',
        },
        {
          label: `${i18n.t('payments.preauth')}:`,
          value: `$${formatAmount(payment.pre_auth_amount)}`,
        },
        {
          label: `${i18n.t('payments.refunded')}:`,
          value: `$${formatAmount(payment.refund_amount)}`,
        },
        {
          label: `${i18n.t('payments.paid')}:`,
          value: `$${formatAmount(payment.charge_amount)}`,
        },
      ],
    },
    {
      title: i18n.t('payments.related_to'),
      items: [
        {
          label: `${formatSourceName(payment?.invoice?.source_name)}:`,
          value: (
            <span className={classes.link}>
              {getDynamicLink(
                payment?.invoice?.source_id,
                payment?.invoice?.source_id,
                payment?.invoice?.source_name
              )}
            </span>
          ),
        },
        // TODO: { label: `${i18n.t("common.period")}:`, value: "Credit" },
        {
          label: `${i18n.t('common.invoice')}:`,
          value: (
            <div className={classes.link}>
              {getDynamicLink(payment?.invoice?.id, payment?.invoice?.id, 'Invoice')}
            </div>
          ),
        },
      ],
    },
  ];

  const transactionData = (transaction) => [
    {
      title: i18n.t('payments.information'),
      items: [
        {
          label: `${i18n.t('transactions.display_name')}:`,
          value: transaction?.display_name || '--',
        },
        {
          label: `${i18n.t('transactions.amount')}:`,
          value: `$${formatAmount(transaction.amount)}`,
        },
        {
          label: `${i18n.t('transactions.customer')}:`,
          value: transaction?.account_customer?.customer_name || '--',
        },
        {
          label: `${i18n.t('transactions.emitter')}:`,
          value: transaction?.account_emitter?.customer_name || '--',
        },
      ],
    },
    {
      title: i18n.t('payments.details'),
      items: [
        {
          label: `${i18n.t('common.date')}:`,
          value: transaction?.modified_time
            ? moment(transaction?.modified_time).format(`${localeFormat}`)
            : '--',
        },
        {
          label: `${i18n.t('payments.preauth')}:`,
          value: `$${formatAmount(transaction.pre_auth_amount)}`,
        },
        {
          label: `${i18n.t('payments.refunded')}:`,
          value: `$${formatAmount(transaction.refund_amount)}`,
        },
        {
          label: `${i18n.t('payments.paid')}:`,
          value: `$${formatAmount(transaction.amount)}`,
        },
      ],
    },
    {
      title: i18n.t('payments.related_to'),
      items: [
        {
          label: `${i18n.t('account_statement.title')}:`,
          value: (
            <div className={classes.link}>
              {getDynamicLink(
                transaction?.account_statement?.id,
                transaction?.account_statement?.id,
                'AccountStatement'
              )}
            </div>
          ),
        },
      ],
    },
  ];

  const getCurrentData = (current) =>
    useTransactions ? transactionData(current) : paymentData(current);

  return (
    <Card title={i18n.t('payments.title')} headerActionType="vertical">
      <>
        {fetchingCurrent ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <Box>
            <Grid
              container
              spacing={1}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Box className={classes.name}>
                  <span className={classes.link}>
                    {i18n.t('payments.id', {
                      id,
                    })}
                  </span>
                </Box>
              </Grid>
              {current?.status && (
                <Grid item className={clsx(classes.status, classes[current?.status])}>
                  {i18n.t(`payment_statuses.${current?.status}`, {
                    defaultValue: current?.status,
                  })}
                </Grid>
              )}
            </Grid>

            <Divider className={classes.divider} light={true} />

            <>
              {getCurrentData(current).map((val, index) => (
                <CollapseCard
                  key={index}
                  title={
                    val?.subtitle ? (
                      <ListTitleComponent title={val?.title} subtitle={val?.subtitle} />
                    ) : (
                      val?.title
                    )
                  }
                  customClasses={collapseCardClasses}
                >
                  <List className={classes.list}>
                    {val?.items?.map((val, index) => {
                      if (Object.prototype.hasOwnProperty.call(val, 'show') && !val?.show) return;
                      return (
                        <ListItem key={index} className={clsx(classes.listItem, val?.classes)}>
                          <ListItemText
                            classes={listClasses}
                            primary={val.label}
                            secondary={
                              <Box
                                display="flex"
                                component="span"
                                flexDirection="column"
                                className={classes.textRight}
                              >
                                <Typography
                                  variant="body2"
                                  component="span"
                                  color="textSecondary"
                                  className={classes?.[val?.classes]}
                                >
                                  {val.value}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </CollapseCard>
              ))}
            </>
          </Box>
        )}
      </>
    </Card>
  );
};

ListTitleComponent.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

PaymentWidget.propTypes = {
  id: PropTypes.number.isRequired,
};

export { PaymentWidget };
